import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Modal, Spinner } from "react-bootstrap";
import ReactMarkdown from "react-markdown";
import {
  FiArchive,
  FiEdit2,
  FiEye,
  FiFileText,
  FiImage,
  FiLink,
  FiList,
  FiMessageSquare,
  FiPlus,
  FiRotateCcw,
  FiSearch,
  FiTrash2,
} from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";

import { getApiUrl } from "../../utils/apiUrl";
import { getCsrfToken } from "../../utils/getCsrfToken";
import "./NovedadesPage.css";

const PAGE_SIZE = 10;
const TYPES = {
  noticias: { singular: "Noticia", plural: "Noticias" },
  testimonios: { singular: "Testimonio", plural: "Testimonios" },
};

const todayValue = () => new Date().toISOString().slice(0, 10);
const emptyForm = (tipo = "noticias") => ({
  tipo,
  title: "",
  date: todayValue(),
  body: "",
  link: "",
  extra_links: [],
});

const firstError = (payload, fallback) => {
  if (!payload || typeof payload !== "object") return fallback;
  const value = Object.values(payload)[0];
  if (Array.isArray(value)) {
    const first = value[0];
    return typeof first === "string" ? first : JSON.stringify(first);
  }
  return typeof value === "string" ? value : fallback;
};

const formatDate = (value) => {
  if (!value) return "Sin fecha";
  return new Intl.DateTimeFormat("es-CL", { dateStyle: "long" }).format(
    new Date(`${value}T12:00:00`),
  );
};

const NovedadImage = ({ item, large = false }) => (
  <div className={`novedad-image${large ? " novedad-image--large" : ""}`}>
    {item?.image ? (
      <img src={item.image} alt={item.title || "Novedad"} />
    ) : (
      <span>{item?.tipo === "testimonios" ? "T" : "N"}</span>
    )}
  </div>
);

const NovedadesPage = () => {
  const { tipo, id } = useParams();
  const navigate = useNavigate();
  const editorRef = useRef(null);
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const [items, setItems] = useState([]);
  const [galleries, setGalleries] = useState([]);
  const [activeType, setActiveType] = useState(
    TYPES[tipo] ? tipo : "noticias",
  );
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [showEditor, setShowEditor] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [imageFile, setImageFile] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [removeVideo, setRemoveVideo] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [galleryId, setGalleryId] = useState("");
  const [newGalleryTitle, setNewGalleryTitle] = useState("");
  const [galleryImage, setGalleryImage] = useState(null);
  const [galleryImageName, setGalleryImageName] = useState("");
  const [galleryError, setGalleryError] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [archivedItems, setArchivedItems] = useState([]);
  const [archivedLoading, setArchivedLoading] = useState(false);
  const [archivedError, setArchivedError] = useState("");
  const [reactivating, setReactivating] = useState("");

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [newsResponse, testimonialsResponse, galleriesResponse] = await Promise.all([
        fetch(getApiUrl("api/novedades/gestion/noticias/"), { credentials: "include" }),
        fetch(getApiUrl("api/novedades/gestion/testimonios/"), { credentials: "include" }),
        fetch(getApiUrl("api/novedades/gestion/galerias/"), { credentials: "include" }),
      ]);
      if (!newsResponse.ok || !testimonialsResponse.ok || !galleriesResponse.ok) {
        throw new Error("No se pudieron cargar las novedades.");
      }
      const [news, testimonials, galleryOptions] = await Promise.all([
        newsResponse.json(),
        testimonialsResponse.json(),
        galleriesResponse.json(),
      ]);
      setItems([...news, ...testimonials]);
      setGalleries(galleryOptions);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (!id || !TYPES[tipo]) {
      setSelected(null);
      return;
    }
    setActiveType(tipo);
    if (loading) return;
    let active = true;
    setDetailLoading(true);
    fetch(getApiUrl(`api/novedades/gestion/${tipo}/${id}/`), {
      credentials: "include",
    })
      .then((response) => {
        if (!response.ok) throw new Error("No se pudo cargar la novedad.");
        return response.json();
      })
      .then((data) => { if (active) setSelected(data); })
      .catch((requestError) => { if (active) setError(requestError.message); })
      .finally(() => { if (active) setDetailLoading(false); });
    return () => { active = false; };
  }, [id, tipo, loading]);

  const filtered = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase("es");
    return items
      .filter((item) => item.tipo === activeType)
      .filter((item) => !normalized || [item.title, item.body, item.grupo_nombre]
        .some((value) => String(value || "").toLocaleLowerCase("es").includes(normalized)))
      .sort((left, right) => (
        String(right.date || "").localeCompare(String(left.date || ""))
        || right.id.localeCompare(left.id)
      ));
  }, [items, activeType, query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const visibleItems = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  useEffect(() => { setCurrentPage(1); }, [activeType, query]);
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  useEffect(() => {
    setGalleryId("");
    setNewGalleryTitle("");
    setGalleryImage(null);
    setGalleryImageName("");
    setGalleryError("");
  }, [selected?.id, selected?.tipo]);

  const syncItem = (updated) => {
    setSelected(updated);
    setItems((current) => current.map((item) => (
      item.id === updated.id && item.tipo === updated.tipo ? updated : item
    )));
    if (updated.grupo_detalle) {
      setGalleries((current) => {
        const gallery = {
          id: updated.grupo_detalle.id,
          title: updated.grupo_detalle.title,
          photos_count: updated.grupo_detalle.photos.length,
          videos_count: current.find((item) => item.id === updated.grupo_detalle.id)?.videos_count || 0,
        };
        return [...current.filter((item) => item.id !== gallery.id), gallery]
          .sort((left, right) => left.title.localeCompare(right.title, "es"));
      });
    }
  };

  const setNovedadActivity = async (item, activa) => {
    const csrfToken = await getCsrfToken();
    const response = await fetch(
      getApiUrl(`api/novedades/gestion/${item.tipo}/${item.id}/`),
      {
        method: "PATCH",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
        body: JSON.stringify({ activa }),
      },
    );
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(firstError(
        data,
        activa ? "No se pudo reactivar la novedad." : "No se pudo archivar la novedad.",
      ));
    }
    return data;
  };

  const openArchived = async () => {
    setShowArchived(true);
    setArchivedLoading(true);
    setArchivedError("");
    try {
      const [newsResponse, testimonialsResponse] = await Promise.all([
        fetch(getApiUrl("api/novedades/gestion/noticias/?activa=false"), { credentials: "include" }),
        fetch(getApiUrl("api/novedades/gestion/testimonios/?activa=false"), { credentials: "include" }),
      ]);
      if (!newsResponse.ok || !testimonialsResponse.ok) {
        throw new Error("No se pudieron cargar las novedades archivadas.");
      }
      const [news, testimonials] = await Promise.all([
        newsResponse.json(),
        testimonialsResponse.json(),
      ]);
      setArchivedItems([...news, ...testimonials].sort((left, right) => (
        String(right.date || "").localeCompare(String(left.date || ""))
        || right.id.localeCompare(left.id)
      )));
    } catch (requestError) {
      setArchivedError(requestError.message);
    } finally {
      setArchivedLoading(false);
    }
  };

  const archiveNovedad = async () => {
    if (!selected || !window.confirm(`¿Archivar "${selected.title}"? Dejará de aparecer en el sitio público.`)) return;
    setBusy(true);
    setError("");
    try {
      const archived = await setNovedadActivity(selected, false);
      setItems((current) => current.filter((item) => !(
        item.id === archived.id && item.tipo === archived.tipo
      )));
      setSelected(null);
      navigate("/novedades");
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  const reactivateNovedad = async (item) => {
    const key = `${item.tipo}-${item.id}`;
    setReactivating(key);
    setArchivedError("");
    try {
      const restored = await setNovedadActivity(item, true);
      setArchivedItems((current) => current.filter((archived) => !(
        archived.id === restored.id && archived.tipo === restored.tipo
      )));
      setItems((current) => [
        ...current.filter((active) => !(
          active.id === restored.id && active.tipo === restored.tipo
        )),
        restored,
      ]);
    } catch (requestError) {
      setArchivedError(requestError.message);
    } finally {
      setReactivating("");
    }
  };

  const updateGallery = async (action, extraPayload = {}) => {
    if (!selected) return;
    setBusy(true);
    setGalleryError("");
    try {
      const csrfToken = await getCsrfToken();
      const isImageUpload = action === "add_image";
      const body = isImageUpload ? new FormData() : JSON.stringify({ action, ...extraPayload });
      const headers = { "X-CSRFToken": csrfToken };
      if (isImageUpload) {
        body.append("action", action);
        body.append("image", galleryImage);
        if (galleryImageName.trim()) body.append("name", galleryImageName.trim());
      } else {
        headers["Content-Type"] = "application/json";
      }
      const response = await fetch(
        getApiUrl(`api/novedades/gestion/${selected.tipo}/${selected.id}/galeria/`),
        { method: "POST", credentials: "include", headers, body },
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(firstError(data, "No se pudo actualizar la galería."));
      syncItem(data);
      setGalleryId("");
      setNewGalleryTitle("");
      setGalleryImage(null);
      setGalleryImageName("");
    } catch (requestError) {
      setGalleryError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  const removeGalleryPhoto = async (photoId) => {
    if (!selected || !window.confirm("¿Quitar esta imagen de la galería?")) return;
    setBusy(true);
    setGalleryError("");
    try {
      const csrfToken = await getCsrfToken();
      const response = await fetch(
        getApiUrl(`api/novedades/gestion/${selected.tipo}/${selected.id}/galeria/fotos/${photoId}/`),
        { method: "DELETE", credentials: "include", headers: { "X-CSRFToken": csrfToken } },
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(firstError(data, "No se pudo quitar la imagen."));
      syncItem(data);
    } catch (requestError) {
      setGalleryError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm(activeType));
    setImageFile(null);
    setVideoFile(null);
    setRemoveImage(false);
    setRemoveVideo(false);
    setShowPreview(false);
    setError("");
    setShowEditor(true);
  };

  const openEdit = () => {
    setEditing(selected);
    setForm({
      tipo: selected.tipo,
      title: selected.title || "",
      date: selected.date || todayValue(),
      body: selected.body || "",
      link: selected.link || "",
      extra_links: selected.extra_links?.map((link) => ({ ...link })) || [],
    });
    setImageFile(null);
    setVideoFile(null);
    setRemoveImage(false);
    setRemoveVideo(false);
    setShowPreview(false);
    setError("");
    setShowEditor(true);
  };

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const applyFormat = (before, after, placeholder) => {
    const textarea = editorRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selection = form.body.slice(start, end) || placeholder;
    const nextBody = `${form.body.slice(0, start)}${before}${selection}${after}${form.body.slice(end)}`;
    updateField("body", nextBody);
    window.setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + before.length, start + before.length + selection.length);
    }, 0);
  };

  const applyLines = (prefix, placeholder) => {
    const textarea = editorRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selection = form.body.slice(start, end) || placeholder;
    const formatted = selection.split("\n").map((line, index) => (
      prefix === "ordered" ? `${index + 1}. ${line}` : `${prefix}${line}`
    )).join("\n");
    updateField("body", `${form.body.slice(0, start)}${formatted}${form.body.slice(end)}`);
    window.setTimeout(() => textarea.focus(), 0);
  };

  const updateExtraLink = (index, field, value) => {
    setForm((current) => ({
      ...current,
      extra_links: current.extra_links.map((link, linkIndex) => (
        linkIndex === index ? { ...link, [field]: value } : link
      )),
    }));
  };

  const saveNovedad = async (event) => {
    event.preventDefault();
    const incompleteLink = form.extra_links.some((link) => (
      (link.name.trim() && !link.url.trim()) || (!link.name.trim() && link.url.trim())
    ));
    if (incompleteLink) {
      setError("Cada enlace adicional necesita un nombre y una URL.");
      return;
    }
    setBusy(true);
    setError("");
    const payload = new FormData();
    payload.append("title", form.title.trim());
    payload.append("date", form.date);
    payload.append("body", form.body.trim());
    payload.append("link", form.link.trim());
    payload.append("extra_links", JSON.stringify(
      form.extra_links.filter((link) => link.name.trim() && link.url.trim()),
    ));
    if (imageFile) payload.append("image", imageFile);
    if (videoFile) payload.append("video", videoFile);
    if (editing && removeImage) payload.append("remove_image", "true");
    if (editing && removeVideo) payload.append("remove_video", "true");

    const targetType = editing?.tipo || form.tipo;
    const endpoint = editing
      ? `api/novedades/gestion/${targetType}/${editing.id}/`
      : `api/novedades/gestion/${targetType}/`;
    try {
      const csrfToken = await getCsrfToken();
      const response = await fetch(getApiUrl(endpoint), {
        method: editing ? "PATCH" : "POST",
        credentials: "include",
        headers: { "X-CSRFToken": csrfToken },
        body: payload,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(firstError(data, "No se pudo guardar la novedad."));
      setItems((current) => {
        const withoutSaved = current.filter((item) => !(item.id === data.id && item.tipo === data.tipo));
        return [...withoutSaved, data];
      });
      setSelected(data);
      setActiveType(data.tipo);
      setShowEditor(false);
      navigate(`/novedades/${data.tipo}/${data.id}`);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="novedades-management-page">
      <section className="novedades-management-hero">
        <div>
          <span>CONTENIDO PÚBLICO</span>
          <h1>Novedades</h1>
          <p>Administra las noticias y testimonios publicados en el sitio web.</p>
        </div>
        <div className="novedades-management-hero-actions">
          <button type="button" onClick={openArchived}><FiArchive /> Archivadas</button>
          <button type="button" onClick={openCreate}><FiPlus /> Agregar novedad</button>
        </div>
      </section>

      <section className="novedades-management-toolbar">
        <div className="novedades-type-tabs" role="tablist" aria-label="Tipo de novedad">
          {Object.entries(TYPES).map(([key, labels]) => (
            <button
              type="button"
              key={key}
              className={activeType === key ? "is-active" : ""}
              onClick={() => setActiveType(key)}
              role="tab"
              aria-selected={activeType === key}
            >
              {key === "noticias" ? <FiFileText /> : <FiMessageSquare />}
              {labels.plural}
              <span>{items.filter((item) => item.tipo === key).length}</span>
            </button>
          ))}
        </div>
        <label className="novedades-management-search">
          <FiSearch />
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={`Buscar en ${TYPES[activeType].plural.toLowerCase()}...`} />
        </label>
      </section>

      {error && !showEditor && <Alert variant="danger">{error}</Alert>}
      {loading ? (
        <div className="novedades-management-state"><Spinner animation="border" /><span>Cargando novedades...</span></div>
      ) : (
        <section className="novedades-management-table">
          <div className="novedades-management-table-header">
              <span aria-hidden="true" /><span>Título</span><span>Tipo</span><span>Fecha</span><span>Visualizaciones</span>
          </div>
          {visibleItems.length ? visibleItems.map((item) => (
            <button
              type="button"
              className="novedades-management-row"
              key={`${item.tipo}-${item.id}`}
              onClick={() => navigate(`/novedades/${item.tipo}/${item.id}`)}
            >
              <NovedadImage item={item} />
              <span className="novedad-row-title"><strong>{item.title}</strong><small>{item.id}</small></span>
              <span className={`novedad-type-badge ${item.tipo}`}>{TYPES[item.tipo].singular}</span>
              <span>{formatDate(item.date)}</span>
              <span className="novedad-views"><FiEye /> <b>{item.visualizaciones || 0}</b></span>
            </button>
          )) : <div className="novedades-management-state">No hay {TYPES[activeType].plural.toLowerCase()} que coincidan.</div>}
          {totalPages > 1 && (
            <nav className="novedades-management-pagination" aria-label="Páginas de novedades">
              <button type="button" disabled={currentPage === 1} onClick={() => setCurrentPage((page) => page - 1)}>Anterior</button>
              <span>Página {currentPage} de {totalPages}</span>
              <button type="button" disabled={currentPage === totalPages} onClick={() => setCurrentPage((page) => page + 1)}>Siguiente</button>
            </nav>
          )}
        </section>
      )}

      {detailLoading && <div className="novedades-detail-loading"><Spinner animation="border" /></div>}
      <Modal
        show={Boolean(selected) && !showEditor}
        onHide={() => !busy && navigate("/novedades")}
        backdrop="static"
        keyboard={!busy}
        dialogClassName="novedad-detail-modal"
      >
        <Modal.Header closeButton={!busy} className="novedad-detail-close" />
        {busy && <div className="novedad-detail-busy"><Spinner animation="border" /><span>Actualizando novedad...</span></div>}
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          {selected && (
            <>
              <section className="novedad-detail-hero">
                <NovedadImage item={selected} large />
                <div>
                  <span className={`novedad-type-badge ${selected.tipo}`}>{TYPES[selected.tipo].singular}</span>
                  <h2>{selected.title}</h2>
                  <p><FiEye /> {selected.visualizaciones || 0} visualizaciones · {formatDate(selected.date)}</p>
                </div>
                <div className="novedad-detail-actions">
                  <button type="button" onClick={openEdit}><FiEdit2 /> Editar</button>
                  <button type="button" className="archive" onClick={archiveNovedad}><FiArchive /> Archivar</button>
                </div>
              </section>

              <article className="novedad-detail-content">
                <div className="novedad-detail-section-title"><FiFileText /><div><span>CONTENIDO</span><h3>Cuerpo de la novedad</h3></div></div>
                {selected.image && <img className="novedad-detail-image" src={selected.image} alt={selected.title} />}
                <div className="novedad-markdown"><ReactMarkdown>{selected.body || ""}</ReactMarkdown></div>
                {selected.video && <video className="novedad-detail-video" src={selected.video} controls />}
                {(selected.link || selected.extra_links?.length > 0) && (
                  <div className="novedad-detail-links">
                    {selected.link && <a href={selected.link} target="_blank" rel="noreferrer"><FiLink /> Enlace principal</a>}
                    {selected.extra_links?.map((link, index) => <a href={link.url} target="_blank" rel="noreferrer" key={`${link.url}-${index}`}><FiLink /> {link.name}</a>)}
                  </div>
                )}
              </article>

              <section className="novedad-gallery-manager">
                <div className="novedad-detail-section-title"><FiImage /><div><span>MULTIMEDIA</span><h3>Galería</h3></div></div>
                {galleryError && <Alert variant="danger">{galleryError}</Alert>}
                {selected.grupo_detalle ? (
                  <>
                    <div className="novedad-gallery-current">
                      <div><strong>{selected.grupo_detalle.title}</strong><span>{selected.grupo_detalle.photos.length} imágenes</span></div>
                      <button type="button" onClick={() => updateGallery("unassign")} disabled={busy}>Desasignar</button>
                    </div>
                    {selected.grupo_detalle.photos.length ? (
                      <div className="novedad-gallery-grid">
                        {selected.grupo_detalle.photos.map((photo) => (
                          <figure key={photo.id}>
                            <img src={photo.image} alt={photo.name || "Imagen de galería"} />
                            <figcaption>{photo.name || "Sin nombre"}</figcaption>
                            <button type="button" onClick={() => removeGalleryPhoto(photo.id)} disabled={busy} title="Quitar imagen"><FiTrash2 /></button>
                          </figure>
                        ))}
                      </div>
                    ) : <p className="novedad-gallery-empty">Esta galería todavía no tiene imágenes.</p>}
                    <div className="novedad-gallery-upload">
                      <label>Nombre de la imagen<input className="form-control" value={galleryImageName} onChange={(event) => setGalleryImageName(event.target.value)} placeholder="Opcional" /></label>
                      <label>Imagen<input className="form-control" type="file" accept="image/*" onChange={(event) => setGalleryImage(event.target.files?.[0] || null)} /></label>
                      <button type="button" disabled={busy || !galleryImage} onClick={() => updateGallery("add_image")}><FiPlus /> Agregar imagen</button>
                    </div>
                  </>
                ) : (
                  <div className="novedad-gallery-setup">
                    <div>
                      <label>Asignar una galería existente<select className="form-select" value={galleryId} onChange={(event) => setGalleryId(event.target.value)}><option value="">Selecciona una galería</option>{galleries.map((gallery) => <option value={gallery.id} key={gallery.id}>{gallery.title} · {gallery.photos_count} imágenes</option>)}</select></label>
                      <button type="button" disabled={busy || !galleryId} onClick={() => updateGallery("assign", { grupo_id: galleryId })}>Asignar</button>
                    </div>
                    <span>o</span>
                    <div>
                      <label>Crear una nueva galería<input className="form-control" value={newGalleryTitle} onChange={(event) => setNewGalleryTitle(event.target.value)} placeholder="Nombre de la galería" /></label>
                      <button type="button" disabled={busy || !newGalleryTitle.trim()} onClick={() => updateGallery("create", { title: newGalleryTitle.trim() })}><FiPlus /> Crear y asignar</button>
                    </div>
                  </div>
                )}
              </section>
            </>
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={showArchived}
        onHide={() => !reactivating && setShowArchived(false)}
        centered
        dialogClassName="novedad-archive-modal"
      >
        <Modal.Header closeButton={!reactivating}>
          <Modal.Title>Novedades archivadas</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="novedad-archive-intro">Estas publicaciones no aparecen en el sitio público. Reactívalas para volver a publicarlas.</p>
          {archivedError && <Alert variant="danger">{archivedError}</Alert>}
          {archivedLoading ? (
            <div className="novedad-archive-empty"><Spinner animation="border" /><span>Cargando novedades archivadas...</span></div>
          ) : archivedItems.length ? (
            <div className="novedad-archive-list">
              {archivedItems.map((item) => {
                const itemKey = `${item.tipo}-${item.id}`;
                return (
                  <article className="novedad-archive-row" key={itemKey}>
                    <NovedadImage item={item} />
                    <div>
                      <strong>{item.title}</strong>
                      <span>{TYPES[item.tipo].singular} · {formatDate(item.date)}</span>
                    </div>
                    <button type="button" onClick={() => reactivateNovedad(item)} disabled={Boolean(reactivating)}>
                      {reactivating === itemKey ? <Spinner animation="border" size="sm" /> : <FiRotateCcw />} Reactivar
                    </button>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="novedad-archive-empty"><FiArchive /><span>No hay novedades archivadas.</span></div>
          )}
        </Modal.Body>
      </Modal>

      <Modal show={showEditor} onHide={() => !busy && setShowEditor(false)} backdrop="static" keyboard={!busy} dialogClassName="novedad-editor-modal">
        <form onSubmit={saveNovedad}>
          <Modal.Header closeButton={!busy}>
            <Modal.Title>{editing ? "Editar novedad" : "Agregar novedad"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {busy && <div className="novedad-editor-busy"><Spinner animation="border" /><span>Guardando novedad...</span></div>}
            {error && <Alert variant="danger">{error}</Alert>}
            <section className="novedad-editor-section">
              <h3>Publicación</h3>
              <div className="novedad-editor-grid">
                <label>Tipo<select className="form-select" value={form.tipo} onChange={(event) => updateField("tipo", event.target.value)} disabled={Boolean(editing)}><option value="noticias">Noticia</option><option value="testimonios">Testimonio</option></select></label>
                <label>Fecha<input className="form-control" type="date" value={form.date} onChange={(event) => updateField("date", event.target.value)} required /></label>
                <label className="wide">Título<input className="form-control" value={form.title} onChange={(event) => updateField("title", event.target.value)} maxLength="255" required /></label>
              </div>
            </section>

            <section className="novedad-editor-section">
              <div className="novedad-editor-section-heading"><div><h3>Contenido</h3><p>Selecciona texto y utiliza la barra para darle formato.</p></div><button type="button" onClick={() => setShowPreview((value) => !value)}><FiEye /> {showPreview ? "Volver a editar" : "Vista previa"}</button></div>
              {!showPreview ? (
                <>
                  <div className="novedad-format-toolbar" aria-label="Formato del contenido">
                    <button type="button" onClick={() => applyFormat("**", "**", "texto destacado")} title="Negrita"><b>B</b></button>
                    <button type="button" onClick={() => applyFormat("*", "*", "texto en cursiva")} title="Cursiva"><i>I</i></button>
                    <button type="button" onClick={() => applyLines("## ", "Título de sección")} title="Título de sección">H2</button>
                    <button type="button" onClick={() => applyLines("- ", "Elemento de lista")} title="Lista"><FiList /></button>
                    <button type="button" onClick={() => applyLines("ordered", "Elemento de lista")} title="Lista numerada">1.</button>
                    <button type="button" onClick={() => applyLines("> ", "Cita destacada")} title="Cita">“ ”</button>
                    <button type="button" onClick={() => applyFormat("[", "](https://)", "texto del enlace")} title="Enlace"><FiLink /></button>
                  </div>
                  <textarea ref={editorRef} className="form-control novedad-body-editor" value={form.body} onChange={(event) => updateField("body", event.target.value)} required />
                </>
              ) : <div className="novedad-editor-preview novedad-markdown"><ReactMarkdown>{form.body || "Escribe contenido para visualizarlo aquí."}</ReactMarkdown></div>}
            </section>

            <section className="novedad-editor-section">
              <h3>Multimedia principal</h3>
              <div className="novedad-editor-grid">
                <div className="novedad-media-field">
                  <label>Imagen principal<input ref={imageInputRef} className="form-control" type="file" accept="image/*" onChange={(event) => { setImageFile(event.target.files?.[0] || null); setRemoveImage(false); }} /></label>
                  {imageFile ? (
                    <div className="novedad-current-media"><FiImage /><span><strong>Nueva imagen</strong><small>{imageFile.name}</small></span><button type="button" onClick={() => { setImageFile(null); if (imageInputRef.current) imageInputRef.current.value = ""; }}><FiTrash2 /> Descartar</button></div>
                  ) : editing?.image ? (
                    <div className={`novedad-current-media${removeImage ? " is-removed" : ""}`}>
                      {!removeImage && <img src={editing.image} alt="Imagen actual" />}
                      <span><strong>{removeImage ? "La imagen se quitará al guardar" : "Imagen actual"}</strong><small>{removeImage ? "Puedes deshacer esta decisión." : "Se conservará si no haces cambios."}</small></span>
                      <button type="button" onClick={() => setRemoveImage((current) => !current)}>{removeImage ? <><FiRotateCcw /> Deshacer</> : <><FiTrash2 /> Quitar</>}</button>
                    </div>
                  ) : <small>Opcional</small>}
                </div>
                <div className="novedad-media-field">
                  <label>Video principal<input ref={videoInputRef} className="form-control" type="file" accept="video/*" onChange={(event) => { setVideoFile(event.target.files?.[0] || null); setRemoveVideo(false); }} /></label>
                  {videoFile ? (
                    <div className="novedad-current-media"><FiFileText /><span><strong>Nuevo video</strong><small>{videoFile.name}</small></span><button type="button" onClick={() => { setVideoFile(null); if (videoInputRef.current) videoInputRef.current.value = ""; }}><FiTrash2 /> Descartar</button></div>
                  ) : editing?.video ? (
                    <div className={`novedad-current-media${removeVideo ? " is-removed" : ""}`}><FiFileText /><span><strong>{removeVideo ? "El video se quitará al guardar" : "Video actual"}</strong><small>{removeVideo ? "Puedes deshacer esta decisión." : "Se conservará si no haces cambios."}</small></span><button type="button" onClick={() => setRemoveVideo((current) => !current)}>{removeVideo ? <><FiRotateCcw /> Deshacer</> : <><FiTrash2 /> Quitar</>}</button></div>
                  ) : <small>Opcional</small>}
                </div>
              </div>
            </section>

            <section className="novedad-editor-section">
              <div className="novedad-editor-section-heading"><div><h3>Enlaces</h3><p>El enlace principal y los botones adicionales son opcionales.</p></div><button type="button" onClick={() => updateField("extra_links", [...form.extra_links, { name: "", url: "" }])}><FiPlus /> Agregar enlace</button></div>
              <label>Enlace principal<input className="form-control" type="url" value={form.link} onChange={(event) => updateField("link", event.target.value)} placeholder="https://..." /></label>
              <div className="novedad-extra-links">
                {form.extra_links.map((link, index) => (
                  <div key={index}>
                    <input className="form-control" value={link.name} onChange={(event) => updateExtraLink(index, "name", event.target.value)} placeholder="Nombre del botón" />
                    <input className="form-control" type="url" value={link.url} onChange={(event) => updateExtraLink(index, "url", event.target.value)} placeholder="https://..." />
                    <button type="button" onClick={() => updateField("extra_links", form.extra_links.filter((_, linkIndex) => linkIndex !== index))} title="Quitar enlace"><FiTrash2 /></button>
                  </div>
                ))}
              </div>
            </section>
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn btn-outline-secondary" onClick={() => setShowEditor(false)} disabled={busy}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? <Spinner animation="border" size="sm" /> : <FiPlus />} Guardar novedad</button>
          </Modal.Footer>
        </form>
      </Modal>
    </main>
  );
};

export default NovedadesPage;
